import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock,
  ChefHat,
  CheckCircle2,
  Utensils,
  RefreshCw,
  Wifi,
  WifiOff,
  Volume2,
} from 'lucide-react';

// Status config with translation support
const getStatusConfig = (t) => ({
  NEW: { label: t('kitchen.new_orders'), class: 'kds-status-new', icon: Clock },
  COOKING: { label: t('kitchen.cooking'), class: 'kds-status-cooking', icon: ChefHat },
  READY: { label: t('kitchen.ready'), class: 'kds-status-ready', icon: CheckCircle2 },
  SERVED: { label: t('orders.status.served'), class: 'kds-status-served', icon: Utensils },
});

const STATIONS = [
  { id: 'all', name: 'All Stations' },
  { id: 'grill', name: 'Grill' },
  { id: 'drinks', name: 'Drinks' },
  { id: 'soup', name: 'Soup' },
  { id: 'prep', name: 'Prep' },
];

export default function Kitchen() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const [orders, setOrders] = useState([]);
  const [activeStation, setActiveStation] = useState('all');
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Initialize STATUS_CONFIG with translation support
  const STATUS_CONFIG = getStatusConfig(t);

  const fetchOrders = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const resp = await api.get('/kds/orders', {
        params: { 
          outlet_id: currentOutlet.id,
          station: activeStation === 'all' ? null : activeStation,
        },
      });
      setOrders(resp.data);
    } catch (error) {
      toast.error(t('messages.error.failed_fetch_orders'));
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id, activeStation]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const playNotificationSound = useCallback(() => {
    if (soundEnabled) {
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {
        // Ignore audio errors
      }
    }
  }, [soundEnabled]);

  const handleWebSocketMessage = useCallback((message) => {
    if (message.event === 'order.created' || message.event === 'order.updated') {
      fetchOrders();
      playNotificationSound();
      toast.info(t('messages.info.new_order_received'));
    } else if (message.event === 'order.status_changed' || message.event === 'order.item_updated') {
      fetchOrders();
    } else if (message.event === 'order.table_moved') {
      // Refresh orders to show updated table number
      fetchOrders();
      toast.info(t('messages.info.order_moved', { 
        number: message.data?.order_number, 
        from: message.data?.from_table, 
        to: message.data?.to_table 
      }));
    }
  }, [fetchOrders, playNotificationSound]);

  const { isConnected } = useWebSocket(currentOutlet?.id, handleWebSocketMessage);

  const updateItemStatus = async (orderId, itemId, currentStatus) => {
    const statusFlow = ['NEW', 'COOKING', 'READY', 'SERVED'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    if (currentIndex >= statusFlow.length - 1) return;

    const nextStatus = statusFlow[currentIndex + 1];
    try {
      await api.put(`/orders/${orderId}/item-status`, null, {
        params: { item_id: itemId, new_status: nextStatus },
      });
      fetchOrders();
      toast.success(t('messages.info.item_status_updated', { status: nextStatus.toLowerCase() }));
    } catch (error) {
      toast.error(t('messages.error.failed_update_item_status'));
    }
  };

  const markOrderReady = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/status`, null, {
        params: { new_status: 'READY' },
      });
      fetchOrders();
      toast.success(t('messages.success.order_ready'));
    } catch (error) {
      toast.error(t('messages.error.failed_to_update'));
    }
  };

  const markOrderServed = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/status`, null, {
        params: { new_status: 'SERVED' },
      });
      fetchOrders();
      toast.success(t('messages.success.order_served'));
    } catch (error) {
      toast.error(t('messages.error.failed_to_update'));
    }
  };

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMinutes = Math.floor((now - created) / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return `${Math.floor(diffMinutes / 60)}h ${diffMinutes % 60}m ago`;
  };

  const getTimeClass = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMinutes = Math.floor((now - created) / (1000 * 60));
    if (diffMinutes > 15) return 'text-red-500';
    if (diffMinutes > 10) return 'text-amber-500';
    return 'text-muted-foreground';
  };

  const getOrderTypeClass = (type) => {
    const classes = {
      DINE_IN: 'order-type-dinein',
      TAKEAWAY: 'order-type-takeaway',
      DELIVERY: 'order-type-delivery',
    };
    return classes[type] || '';
  };

  const filteredOrders = orders.filter(order => {
    if (activeStation === 'all') return true;
    return order.items?.some(item => item.kitchen_station === activeStation);
  });

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">Please select an outlet first</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="kitchen-display">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-card">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ChefHat className="h-7 w-7" />
            Kitchen Display
          </h1>
          <Badge variant={isConnected ? 'default' : 'destructive'} className="gap-1">
            {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {isConnected ? 'Live' : 'Offline'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={soundEnabled ? 'default' : 'outline'}
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            data-testid="sound-toggle"
          >
            <Volume2 className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={fetchOrders} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Station Tabs */}
      <Tabs value={activeStation} onValueChange={setActiveStation} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start h-auto flex-wrap gap-1 p-2 bg-muted/50 rounded-none">
          {STATIONS.map((station) => (
            <TabsTrigger
              key={station.id}
              value={station.id}
              className="text-lg px-6 py-3"
              data-testid={`station-${station.id}`}
            >
              {station.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeStation} className="flex-1 m-0 overflow-hidden">
          <ScrollArea className="h-full p-4">
            {filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
                <ChefHat className="h-16 w-16 mb-4 opacity-50" />
                <p className="text-xl">No active orders</p>
                <p className="text-sm">New orders will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredOrders.map((order) => (
                  <Card
                    key={order.id}
                    className="overflow-hidden"
                    data-testid={`kds-order-${order.order_number}`}
                  >
                    <CardHeader className="pb-3 bg-muted/30">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-2xl font-mono">
                            #{order.order_number}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={getOrderTypeClass(order.order_type)}>
                              {order.order_type.replace('_', ' ')}
                            </Badge>
                            {order.table_number && (
                              <Badge variant="secondary">{order.table_number}</Badge>
                            )}
                            {order.is_qr_order && (
                              <Badge className="bg-purple-600 text-white">QR ORDER</Badge>
                            )}
                          </div>
                        </div>
                        <div className={`text-right ${getTimeClass(order.created_at)}`}>
                          <Clock className="h-4 w-4 inline mr-1" />
                          <span className="font-mono text-lg">{getTimeSince(order.created_at)}</span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-3">
                        {order.items
                          ?.filter(item => activeStation === 'all' || item.kitchen_station === activeStation)
                          .map((item) => {
                            const StatusIcon = STATUS_CONFIG[item.kitchen_status]?.icon || Clock;
                            return (
                              <div
                                key={item.id}
                                className={`p-3 rounded-md border-2 cursor-pointer transition-all hover:scale-[1.02] ${STATUS_CONFIG[item.kitchen_status]?.class || ''}`}
                                onClick={() => updateItemStatus(order.id, item.id, item.kitchen_status)}
                                data-testid={`kds-item-${item.id}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <p className="font-bold text-lg">
                                      {item.quantity}x {item.menu_item_name}
                                    </p>
                                    {item.variant_name && (
                                      <p className="text-sm opacity-80">{item.variant_name}</p>
                                    )}
                                    {item.add_on_names?.length > 0 && (
                                      <p className="text-sm opacity-80">+ {item.add_on_names.join(', ')}</p>
                                    )}
                                    {item.notes && (
                                      <p className="text-sm font-medium mt-1 text-primary">
                                        Note: {item.notes}
                                      </p>
                                    )}
                                  </div>
                                  <Badge variant="outline" className="gap-1 shrink-0">
                                    <StatusIcon className="h-3 w-3" />
                                    {STATUS_CONFIG[item.kitchen_status]?.label}
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                      </div>

                      {/* Action Buttons based on order/item status */}
                      <div className="mt-4 space-y-2">
                        {/* Mark All Ready - when all items are READY but order is still CONFIRMED/PREPARING */}
                        {order.items?.every(i => 
                          activeStation !== 'all' 
                            ? i.kitchen_station !== activeStation || i.kitchen_status === 'READY' 
                            : i.kitchen_status === 'READY'
                        ) && order.status !== 'READY' && (
                          <Button
                            className="w-full bg-green-600 hover:bg-green-700"
                            onClick={() => markOrderReady(order.id)}
                            data-testid={`mark-ready-${order.order_number}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Mark Order Ready
                          </Button>
                        )}

                        {/* Mark as Served - when order is READY */}
                        {order.status === 'READY' && (
                          <Button
                            className="w-full bg-purple-600 hover:bg-purple-700"
                            onClick={() => markOrderServed(order.id)}
                            data-testid={`mark-served-${order.order_number}`}
                          >
                            <Utensils className="h-4 w-4 mr-2" />
                            Mark as Served
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
